// src/journey/MyJourney.tsx
//
// BekalNanti.ID — Perencanaan Workspace
// Clear mental model: Me → People I manage → Their Plans → Required Data → Next Action
// Unified 4-Tab Navigation: Beranda | Orang | Rencana | Keuangan

import React, { useState, useEffect, useCallback } from 'react'
import { User } from '@supabase/supabase-js'
import {
  JourneyData,
  NextActionItem,
  PersonRecord,
  ProfileRecord,
} from './types'
import {
  fetchJourneyData,
  updateGoal,
  addGoal,
  deleteGoal,
  getPlanningStages,
  determineNextAction,
  getMissingInformation,
} from '../lib/journeyService'
import { PlanningProgress } from './PlanningProgress'
import { WhatWeKnowCard } from './WhatWeKnowCard'
import { WhatWeNeedCard } from './WhatWeNeedCard'
import { NextActionCard } from './NextActionCard'
import { GoalEditorModal } from './GoalEditorModal'
import { FinancialRealityModal } from '../finance/FinancialRealityModal'
import { TargetSpecificationModal } from '../targets/TargetSpecificationModal'
import { CostFundingModal } from '../cost/CostFundingModal'
import { CalculationWorkspaceModal } from '../calculation/CalculationWorkspaceModal'
import { RealityCheckModal } from '../realityCheck/RealityCheckModal'
import { PeopleView } from '../people/PeopleView'
import { PersonModal } from '../people/PersonModal'
import { PlansView } from '../plans/PlansView'
import { FinanceView } from '../finance/FinanceView'
import { MyProfileModal } from '../components/MyProfileModal'
import { HouseholdNameModal } from '../components/HouseholdNameModal'
import { unarchivePerson } from '../lib/peopleService'
import { PetaEsokModal } from '../petaEsok/PetaEsokModal'
import { BottomNavBar, NavTabId } from '../components/BottomNavBar'
import { TunasIcon } from '../components/BekalNantiLogo'
import {
  Compass,
  RotateCcw,
  Loader2,
  AlertCircle,
  LogIn,
  SlidersHorizontal,
  DollarSign,
  Target,
  Users,
} from 'lucide-react'

export type JourneyTab = NavTabId

interface MyJourneyProps {
  user: User | null
  onOpenAuth: (mode?: 'signin' | 'signup') => void
  onReturnToOnboarding: () => void
  activeTab?: JourneyTab
  onSelectTab?: (tab: JourneyTab) => void
  isProfileOpenFromApp?: boolean
  onCloseProfileFromApp?: () => void
  onProfileUpdated?: (updated: ProfileRecord) => void
}

export const MyJourney: React.FC<MyJourneyProps> = ({
  user,
  onOpenAuth,
  onReturnToOnboarding,
  activeTab: externalTab,
  onSelectTab: externalOnSelectTab,
  isProfileOpenFromApp,
  onCloseProfileFromApp,
  onProfileUpdated,
}) => {
  const [journey, setJourney] = useState<JourneyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Navigation tab state (controlled or uncontrolled)
  const [internalTab, setInternalTab] = useState<JourneyTab>('beranda')
  const currentTab = externalTab ?? internalTab
  const setCurrentTab = (tab: JourneyTab) => {
    if (externalOnSelectTab) {
      externalOnSelectTab(tab)
    } else {
      setInternalTab(tab)
    }
  }

  // Modals state
  const [isGoalEditorOpen, setIsGoalEditorOpen] = useState(false)
  const [goalEditorInitialPersonId, setGoalEditorInitialPersonId] = useState<string | null>(null)
  const [goalEditorStartInAddMode, setGoalEditorStartInAddMode] = useState(false)

  const [isPersonModalOpen, setIsPersonModalOpen] = useState(false)
  const [personToEdit, setPersonToEdit] = useState<PersonRecord | null>(null)

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [isHouseholdModalOpen, setIsHouseholdModalOpen] = useState(false)

  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false)

  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false)
  const [targetModalGoalId, setTargetModalGoalId] = useState<string | null>(null)

  const [isCostFundingModalOpen, setIsCostFundingModalOpen] = useState(false)
  const [costFundingModalGoalId, setCostFundingModalGoalId] = useState<string | null>(null)

  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false)
  const [calculationModalGoalId, setCalculationModalGoalId] = useState<string | null>(null)

  const [isRealityCheckModalOpen, setIsRealityCheckModalOpen] = useState(false)
  const [realityCheckModalGoalId, setRealityCheckModalGoalId] = useState<string | null>(null)

  const [isPetaEsokModalOpen, setIsPetaEsokModalOpen] = useState(false)
  const [petaEsokGoalId, setPetaEsokGoalId] = useState<string | null>(null)

  // Active goal selection for dashboard scoping
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null)

  // Sync external profile open trigger from App header
  useEffect(() => {
    if (isProfileOpenFromApp) {
      setIsProfileModalOpen(true)
    }
  }, [isProfileOpenFromApp])

  const loadData = useCallback(async (isSilent = false) => {
    if (!user) {
      setLoading(false)
      return
    }

    if (!isSilent) setLoading(true)
    setError(null)
    try {
      const data = await fetchJourneyData(user)
      setJourney(data)
    } catch (err: any) {
      setError(err.message || 'Gagal memuat perencanaan.')
    } finally {
      if (!isSilent) setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleUpdateGoal = async (
    goalId: string,
    updates: {
      name: string
      goal_type: string
      priority: 'high' | 'medium' | 'low'
      person_id: string | null
    }
  ) => {
    await updateGoal(goalId, updates)
    await loadData(true)
  }

  const handleAddGoal = async (
    householdId: string,
    goal: {
      name: string
      goal_type: string
      priority: 'high' | 'medium' | 'low'
      person_id: string | null
    }
  ) => {
    await addGoal(householdId, goal)
    await loadData(true)
  }

  const handleDeleteGoal = async (goalId: string) => {
    await deleteGoal(goalId)
    await loadData(true)
  }

  const handleRestorePerson = async (personId: string) => {
    await unarchivePerson(personId)
    await loadData(true)
  }

  const handleExecuteAction = (action: NextActionItem) => {
    const targetGoalId =
      action.goalId ||
      (selectedGoalId && journey?.goals.some((g) => g.id === selectedGoalId)
        ? selectedGoalId
        : journey?.goals[0]?.id || null)

    if (action.ctaAction === 'financial_reality') {
      setIsFinancialModalOpen(true)
    } else if (action.ctaAction === 'target_specification') {
      setTargetModalGoalId(targetGoalId)
      setIsTargetModalOpen(true)
    } else if (action.ctaAction === 'cost_funding' || action.ctaAction === 'education_cost') {
      setCostFundingModalGoalId(targetGoalId)
      setIsCostFundingModalOpen(true)
    } else if (action.ctaAction === 'run_calculation') {
      setCalculationModalGoalId(targetGoalId)
      setIsCalculationModalOpen(true)
    } else if (action.ctaAction === 'reality_check') {
      setRealityCheckModalGoalId(targetGoalId)
      setIsRealityCheckModalOpen(true)
    } else if (action.ctaAction === 'peta_esok') {
      setPetaEsokGoalId(targetGoalId)
      setIsPetaEsokModalOpen(true)
    } else if (action.ctaAction === 'edit_goal' || action.ctaAction === 'add_goal') {
      setGoalEditorInitialPersonId(null)
      setGoalEditorStartInAddMode(action.ctaAction === 'add_goal')
      setIsGoalEditorOpen(true)
    }
  }

  // Loading state (only show full spinner on initial fetch when no journey data yet)
  if (loading && !journey) {
    return (
      <div className="py-20 flex flex-col items-center justify-center space-y-3 text-center">
        <Loader2 size={32} className="animate-spin text-[#0EA5A4]" />
        <p className="text-sm font-semibold text-slate-700">
          Memuat Perencanaan Anda...
        </p>
        <p className="text-xs text-slate-400">
          Menyiapkan ruang perencanaan Anda...
        </p>
      </div>
    )
  }

  // Guest State / Not Logged In
  if (!user) {
    return (
      <div className="rounded-3xl border border-slate-200/90 bg-white p-6 shadow-sm text-center space-y-5 max-w-md mx-auto my-6">
        <div className="w-12 h-12 rounded-2xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center mx-auto shadow-xs">
          <Compass size={28} />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-xl font-bold text-slate-900">
            Masuk ke Perencanaan
          </h2>
          <p className="text-xs text-slate-500 leading-relaxed">
            Perencanaan tersimpan aman di akun Anda. Masuk atau buat akun baru untuk melanjutkan.
          </p>
        </div>

        <div className="space-y-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenAuth('signin')}
            className="w-full py-3 px-4 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <LogIn size={16} />
            <span>Masuk ke Akun</span>
          </button>
          <button
            type="button"
            onClick={onReturnToOnboarding}
            className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
          >
            Kembali ke Onboarding
          </button>
        </div>
      </div>
    )
  }

  // Error State with Retry
  if (error || !journey) {
    return (
      <div className="rounded-3xl border border-rose-200 bg-white p-6 shadow-sm text-center space-y-4 max-w-md mx-auto my-6">
        <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
          <AlertCircle size={28} />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-lg font-bold text-slate-900">
            {error ? 'Gagal Memuat Perencanaan' : 'Belum Ada Rencana yang Tersimpan'}
          </h2>
          <p className="text-xs text-slate-500 leading-relaxed">
            {error || 'Akun Anda belum memiliki data perencanaan. Mulai dari alur awal untuk membuat rencana.'}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            onClick={() => loadData()}
            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors cursor-pointer"
          >
            Coba Lagi
          </button>
          <button
            type="button"
            onClick={onReturnToOnboarding}
            className="px-4 py-2.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-colors cursor-pointer shadow-2xs"
          >
            Mulai Onboarding
          </button>
        </div>
      </div>
    )
  }

  // Scoped active goal for Beranda progress
  const effectiveSelectedGoalId =
    (selectedGoalId && journey.goals.some((g) => g.id === selectedGoalId))
      ? selectedGoalId
      : journey.goals[0]?.id || null

  // Compute deterministic stages and actions for Beranda
  const stages = getPlanningStages(journey, effectiveSelectedGoalId)
  const nextAction = determineNextAction(journey, effectiveSelectedGoalId)
  const missingItems = getMissingInformation(journey, effectiveSelectedGoalId)
  const displayName = journey.profile.displayName || user.email?.split('@')[0] || 'Keluarga'

  const activePersons = journey.persons.filter((p) => p.status !== 'archived')
  const totalGoals = journey.goals.length

  return (
    <div className="space-y-5 text-left pb-20">
      {/* 4-Tab Navigation Header / Switcher (Desktop / Tablet) */}
      <div className="hidden sm:flex w-full items-center justify-between p-1 bg-slate-100/90 border border-slate-200/80 rounded-2xl text-xs font-semibold">
        <button
          type="button"
          onClick={() => setCurrentTab('beranda')}
          className={`flex-1 py-2 px-3 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
            currentTab === 'beranda'
              ? 'bg-white text-[#0F766E] shadow-2xs font-bold'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Compass size={15} className={currentTab === 'beranda' ? 'text-[#0EA5A4]' : 'text-slate-400'} />
          <span>Beranda</span>
        </button>

        <button
          type="button"
          onClick={() => setCurrentTab('orang')}
          className={`flex-1 py-2 px-3 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
            currentTab === 'orang'
              ? 'bg-white text-[#0F766E] shadow-2xs font-bold'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users size={15} className={currentTab === 'orang' ? 'text-[#0EA5A4]' : 'text-slate-400'} />
          <span>Orang</span>
          {activePersons.length > 0 && (
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                currentTab === 'orang'
                  ? 'bg-teal-50 text-[#0F766E]'
                  : 'bg-slate-200 text-slate-600'
              }`}
            >
              {activePersons.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setCurrentTab('rencana')}
          className={`flex-1 py-2 px-3 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
            currentTab === 'rencana'
              ? 'bg-white text-[#0F766E] shadow-2xs font-bold'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Target size={15} className={currentTab === 'rencana' ? 'text-[#0EA5A4]' : 'text-slate-400'} />
          <span>Rencana</span>
          {totalGoals > 0 && (
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                currentTab === 'rencana'
                  ? 'bg-teal-50 text-[#0F766E]'
                  : 'bg-slate-200 text-slate-600'
              }`}
            >
              {totalGoals}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setCurrentTab('keuangan')}
          className={`flex-1 py-2 px-3 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
            currentTab === 'keuangan'
              ? 'bg-white text-[#0F766E] shadow-2xs font-bold'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <DollarSign size={15} className={currentTab === 'keuangan' ? 'text-[#0EA5A4]' : 'text-slate-400'} />
          <span>Keuangan</span>
        </button>
      </div>

      {/* ========================================================= */}
      {/* TAB CONTENT 1: BERANDA                                     */}
      {/* ========================================================= */}
      {currentTab === 'beranda' && (
        <div className="space-y-5 animate-in fade-in duration-150">
          {/* Top Banner / Heading */}
          <div className="rounded-3xl bg-gradient-to-br from-teal-50/80 via-white to-emerald-50/40 border border-teal-100/80 p-5 shadow-2xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-teal-200 text-[#0F766E] text-[11px] font-bold shadow-2xs">
                <TunasIcon size={16} />
                <span>Rencana Masa Depan Keluarga</span>
              </span>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setTargetModalGoalId(effectiveSelectedGoalId)
                    setIsTargetModalOpen(true)
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 hover:text-[#0F766E] hover:border-teal-200 text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                >
                  <Target size={13} className="text-[#0EA5A4]" />
                  <span>Spesifikasi Sasaran</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsFinancialModalOpen(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 hover:text-[#0F766E] hover:border-teal-200 text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                >
                  <DollarSign size={13} className="text-[#0EA5A4]" />
                  <span>Realita Finansial</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGoalEditorInitialPersonId(null)
                    setGoalEditorStartInAddMode(false)
                    setIsGoalEditorOpen(true)
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 hover:text-[#0F766E] hover:border-teal-200 text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                >
                  <SlidersHorizontal size={13} className="text-[#0EA5A4]" />
                  <span>Kelola Tujuan</span>
                </button>
              </div>
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
                Perjalanan Perencanaan, {displayName}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">
                Ini adalah ruang pemetaan rencana masa depanmu. BekalNanti.ID mencatat apa yang sudah pasti dan memandu apa yang masih perlu disiapkan.
              </p>
            </div>

            {/* Goal Selector (when multiple goals exist) */}
            {journey.goals.length > 1 && (
              <div className="pt-2 border-t border-teal-100/60 flex items-center gap-2 overflow-x-auto text-xs">
                <span className="text-teal-800 text-[11px] font-semibold flex-shrink-0">
                  Sasaran yang Dipantau:
                </span>
                <div className="flex items-center gap-1.5">
                  {journey.goals.map((g) => {
                    const isSelected = g.id === effectiveSelectedGoalId
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setSelectedGoalId(g.id)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-[#0EA5A4] text-white shadow-2xs'
                            : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {g.name}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 1. Next Meaningful Action (Langkah Bermakna Berikutnya) */}
          <NextActionCard action={nextAction} onExecuteAction={handleExecuteAction} />

          {/* 2. Planning Journey Progress Model (8 Tahap Nyata) */}
          <PlanningProgress stages={stages} />

          {/* 3. What We Know Card (Yang Sudah Diketahui) */}
          <WhatWeKnowCard
            journey={journey}
            onEditHouseholdName={() => setIsHouseholdModalOpen(true)}
            onEditGoalClick={() => {
              setGoalEditorInitialPersonId(null)
              setGoalEditorStartInAddMode(false)
              setIsGoalEditorOpen(true)
            }}
            onOpenFinancialReality={() => setIsFinancialModalOpen(true)}
            onOpenTargetSpecification={(goalId?: string) => {
              setTargetModalGoalId(goalId || effectiveSelectedGoalId)
              setIsTargetModalOpen(true)
            }}
            onOpenCostFunding={(goalId?: string) => {
              setCostFundingModalGoalId(goalId || effectiveSelectedGoalId)
              setIsCostFundingModalOpen(true)
            }}
            onOpenCalculation={(goalId?: string) => {
              setCalculationModalGoalId(goalId || effectiveSelectedGoalId)
              setIsCalculationModalOpen(true)
            }}
            onOpenRealityCheck={(goalId?: string) => {
              setRealityCheckModalGoalId(goalId || effectiveSelectedGoalId)
              setIsRealityCheckModalOpen(true)
            }}
            onOpenPetaEsok={(goalId?: string) => {
              setPetaEsokGoalId(goalId || effectiveSelectedGoalId)
              setIsPetaEsokModalOpen(true)
            }}
          />

          {/* 4. What We Don't Know Yet Card (Yang Masih Dibutuhkan) */}
          <WhatWeNeedCard
            missingItems={missingItems}
            onAddressNextClick={() => handleExecuteAction(nextAction)}
          />

          {/* Navigation & Onboarding Adjustment Footer Bar */}
          <div className="pt-2 flex items-center justify-between text-xs text-slate-500 border-t border-slate-200/70">
            <button
              type="button"
              onClick={onReturnToOnboarding}
              className="inline-flex items-center gap-1.5 py-2 px-3 rounded-xl hover:bg-slate-100 hover:text-slate-800 transition-colors cursor-pointer"
            >
              <RotateCcw size={13} />
              <span>Sesuaikan Jawaban Onboarding</span>
            </button>

            <span className="text-[11px] text-slate-400">
              BekalNanti • Platform Perencanaan Masa Depan
            </span>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB CONTENT 2: ORANG (PEOPLE MANAGEMENT)                 */}
      {/* ========================================================= */}
      {currentTab === 'orang' && (
        <div className="animate-in fade-in duration-150">
          <PeopleView
            journey={journey}
            onOpenAddPerson={() => {
              setPersonToEdit(null)
              setIsPersonModalOpen(true)
            }}
            onEditPerson={(p) => {
              setPersonToEdit(p)
              setIsPersonModalOpen(true)
            }}
            onOpenAddPlanForPerson={(personId) => {
              setGoalEditorInitialPersonId(personId)
              setGoalEditorStartInAddMode(true)
              setIsGoalEditorOpen(true)
            }}
            onOpenPlan={(goal) => {
              setTargetModalGoalId(goal.id)
              setIsTargetModalOpen(true)
            }}
            onEditMyProfile={() => setIsProfileModalOpen(true)}
            onRestorePerson={handleRestorePerson}
          />
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB CONTENT 3: RENCANA (PLANS REPOSITORY)                */}
      {/* ========================================================= */}
      {currentTab === 'rencana' && (
        <div className="animate-in fade-in duration-150">
          <PlansView
            journey={journey}
            onOpenAddPlan={(prefillPersonId) => {
              setGoalEditorInitialPersonId(prefillPersonId || null)
              setGoalEditorStartInAddMode(true)
              setIsGoalEditorOpen(true)
            }}
            onEditPlan={(goal) => {
              setGoalEditorInitialPersonId(goal.personId || null)
              setGoalEditorStartInAddMode(false)
              setIsGoalEditorOpen(true)
            }}
            onOpenTargetSpecification={(goalId) => {
              setTargetModalGoalId(goalId)
              setIsTargetModalOpen(true)
            }}
            onOpenCostFunding={(goalId) => {
              setCostFundingModalGoalId(goalId)
              setIsCostFundingModalOpen(true)
            }}
            onOpenCalculation={(goalId) => {
              setCalculationModalGoalId(goalId)
              setIsCalculationModalOpen(true)
            }}
            onOpenRealityCheck={(goalId) => {
              setRealityCheckModalGoalId(goalId)
              setIsRealityCheckModalOpen(true)
            }}
            onOpenPetaEsok={(goalId) => {
              setPetaEsokGoalId(goalId)
              setIsPetaEsokModalOpen(true)
            }}
          />
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB CONTENT 4: KEUANGAN (FINANCIAL REALITY)               */}
      {/* ========================================================= */}
      {currentTab === 'keuangan' && (
        <div className="animate-in fade-in duration-150">
          <FinanceView
            financialReality={journey.financialReality}
            onOpenFinancialModal={() => setIsFinancialModalOpen(true)}
          />
        </div>
      )}

      {/* ========================================================= */}
      {/* MOBILE PERSISTENT BOTTOM NAVIGATION BAR                   */}
      {/* ========================================================= */}
      <BottomNavBar
        activeTab={currentTab}
        onSelectTab={setCurrentTab}
        peopleCount={activePersons.length}
        planCount={totalGoals}
      />

      {/* ========================================================= */}
      {/* MODALS & WORKSPACES                                       */}
      {/* ========================================================= */}

      {/* Goal Editor Modal */}
      <GoalEditorModal
        isOpen={isGoalEditorOpen}
        goals={journey.goals}
        persons={journey.persons}
        householdId={journey.household.id}
        initialPersonId={goalEditorInitialPersonId}
        startInAddMode={goalEditorStartInAddMode}
        onClose={() => {
          setIsGoalEditorOpen(false)
          setGoalEditorInitialPersonId(null)
          setGoalEditorStartInAddMode(false)
        }}
        onUpdateGoal={handleUpdateGoal}
        onAddGoal={handleAddGoal}
        onDeleteGoal={handleDeleteGoal}
        onRefreshPersons={async () => {
          await loadData(true)
        }}
      />

      {/* Person Add / Edit Modal */}
      <PersonModal
        isOpen={isPersonModalOpen}
        householdId={journey.household.id}
        personToEdit={personToEdit}
        onClose={() => {
          setIsPersonModalOpen(false)
          setPersonToEdit(null)
        }}
        onSuccess={async () => {
          await loadData(true)
        }}
      />

      {/* My Profile Modal */}
      <MyProfileModal
        isOpen={isProfileModalOpen}
        profile={journey.profile}
        userEmail={user.email}
        onClose={() => {
          setIsProfileModalOpen(false)
          if (onCloseProfileFromApp) {
            onCloseProfileFromApp()
          }
        }}
        onUpdateSuccess={(updated) => {
          setJourney((prev) => (prev ? { ...prev, profile: updated } : null))
          if (onProfileUpdated) {
            onProfileUpdated(updated)
          }
          if (onCloseProfileFromApp) {
            onCloseProfileFromApp()
          }
        }}
      />

      {/* Financial Reality Workspace Modal */}
      <FinancialRealityModal
        isOpen={isFinancialModalOpen}
        householdId={journey.household.id}
        initialData={journey.financialReality}
        onClose={() => setIsFinancialModalOpen(false)}
        onRefreshJourney={() => loadData(true)}
      />

      {/* Target Specification Workspace Modal */}
      <TargetSpecificationModal
        isOpen={isTargetModalOpen}
        goals={journey.goals}
        initialGoalId={targetModalGoalId}
        onClose={() => setIsTargetModalOpen(false)}
        onRefreshJourney={() => loadData(true)}
      />

      {/* Education Cost & Funding Workspace Modal */}
      <CostFundingModal
        isOpen={isCostFundingModalOpen}
        goals={journey.goals}
        initialGoalId={costFundingModalGoalId}
        onClose={() => setIsCostFundingModalOpen(false)}
        onSaveSuccess={() => loadData(true)}
      />

      {/* Deterministic Calculation Workspace Modal */}
      <CalculationWorkspaceModal
        isOpen={isCalculationModalOpen}
        goal={
          (calculationModalGoalId
            ? journey.goals.find((g) => g.id === calculationModalGoalId)
            : journey.goals.find((g) => g.goalType === 'education') || journey.goals[0]) || null
        }
        journey={journey}
        onClose={() => setIsCalculationModalOpen(false)}
        onProceedToRealityCheck={() => {
          const targetGoal = calculationModalGoalId
            ? journey.goals.find((g) => g.id === calculationModalGoalId)
            : journey.goals.find((g) => g.goalType === 'education') || journey.goals[0]
          setRealityCheckModalGoalId(targetGoal?.id || null)
          setIsRealityCheckModalOpen(true)
        }}
      />

      {/* Shared Reality Check Layer Modal */}
      <RealityCheckModal
        isOpen={isRealityCheckModalOpen}
        goal={
          (realityCheckModalGoalId
            ? journey.goals.find((g) => g.id === realityCheckModalGoalId)
            : journey.goals.find((g) => g.goalType === 'education') || journey.goals[0]) || null
        }
        journey={journey}
        onClose={() => setIsRealityCheckModalOpen(false)}
        onOpenCostFunding={() => {
          const currentGoalId =
            realityCheckModalGoalId ||
            (journey.goals.find((g) => g.goalType === 'education') || journey.goals[0])?.id ||
            null
          setCostFundingModalGoalId(currentGoalId)
          setIsCostFundingModalOpen(true)
        }}
        onOpenCalculation={() => {
          const currentGoalId =
            realityCheckModalGoalId ||
            (journey.goals.find((g) => g.goalType === 'education') || journey.goals[0])?.id ||
            null
          setCalculationModalGoalId(currentGoalId)
          setIsCalculationModalOpen(true)
        }}
      />

      {/* Signature Experience: PetaEsok Entry Modal */}
      <PetaEsokModal
        isOpen={isPetaEsokModalOpen}
        journey={journey}
        goal={petaEsokGoalId ? journey.goals.find((g) => g.id === petaEsokGoalId) : null}
        onClose={() => {
          setIsPetaEsokModalOpen(false)
          setPetaEsokGoalId(null)
        }}
        onOpenRealityCheck={(goalId?: string) => {
          setIsPetaEsokModalOpen(false)
          setRealityCheckModalGoalId(goalId || null)
          setIsRealityCheckModalOpen(true)
        }}
        onOpenCalculation={(goalId?: string) => {
          setIsPetaEsokModalOpen(false)
          setCalculationModalGoalId(goalId || null)
          setIsCalculationModalOpen(true)
        }}
        onOpenCostFunding={() => {
          setIsPetaEsokModalOpen(false)
          setIsCostFundingModalOpen(true)
        }}
      />

      {/* Household Planning Space Name Modal */}
      {isHouseholdModalOpen && journey && (
        <HouseholdNameModal
          isOpen={isHouseholdModalOpen}
          householdId={journey.household.id}
          currentName={journey.household.name}
          onClose={() => setIsHouseholdModalOpen(false)}
          onUpdateSuccess={(newName) => {
            setJourney((prev) => (prev ? { ...prev, household: { ...prev.household, name: newName } } : null))
            loadData(true)
          }}
        />
      )}
    </div>
  )
}
